import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronsUpDown, Loader2, Trash2, X } from 'lucide-react';
import { ShareAndroid } from 'iconoir-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import { VisibilityField } from 'components/blocks/visibility-field';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Popover, PopoverAnchor, PopoverContent } from 'components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useGetAgentShare, useSaveAgentShare } from 'features/chat/hooks/use-agents';
import {
  DEFAULT_SHARE_GRANT,
  type OrgGrantInput,
  type ShareGrant,
  type ShareParticipantInput,
  type SharePermissionType,
} from 'features/chat/agent-chat-module/types/agent-sharing.type';
import { useGetUsersQuery } from 'features/iam/hooks/use-iam';
import type { IamData } from 'features/iam/services/user-service';
import { HttpError } from 'lib/https';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useCommonStore } from 'state/store/common/useCommonStore';
import {
  DEFAULT_RESOURCE_VISIBILITY,
  type ResourceVisibility,
} from 'types/visibility';

type ParticipantDraft = ShareParticipantInput & {
  displayName: string;
  email?: string;
  avatarUrl?: string | null;
};

type AgentShareModalProps = {
  agentId: string;
  agentName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const displayNameFor = (user: IamData) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
  user.userName ||
  user.email ||
  user.itemId;

const initialsFor = (name: string, email?: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  if (parts[0]?.length) return parts[0].slice(0, 2).toUpperCase();
  return (email?.[0] ?? '?').toUpperCase();
};

function UserInitials({
  name,
  email,
  size = 'md',
}: {
  name: string;
  email?: string;
  size?: 'sm' | 'md';
}) {
  const sizeClass = size === 'sm' ? 'h-8 w-8 text-[11px]' : 'h-9 w-9 text-xs';
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-grey-100 font-semibold uppercase text-grey-700',
        'dark:bg-white/15 dark:text-grey-100',
        sizeClass
      )}
    >
      {initialsFor(name, email)}
    </span>
  );
}

/** Plain readable copy when UILM keys are missing or still untranslated. */
const shareText = (
  t: TFunction,
  key: string,
  fallback: string,
  options?: Record<string, unknown>
) => {
  const value = t(key, { defaultValue: fallback, ...options });
  if (!value || value === key || /^[A-Z][A-Z0-9_]+$/.test(value)) {
    return fallback;
  }
  return value;
};

const grantFromDraft = (draft: ParticipantDraft): ShareGrant => {
  if (draft.permissionType !== 'editor') {
    return DEFAULT_SHARE_GRANT();
  }
  return {
    permissionType: 'editor',
    allowSubagents: !!draft.allowSubagents,
    allowSkills: !!draft.allowSkills,
    allowTools: !!draft.allowTools,
    allowKb: !!draft.allowKb,
  };
};

export function AgentShareModal({
  agentId,
  agentName,
  open,
  onOpenChange,
}: AgentShareModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const currentUserId = useCommonStore((s) => s.user?.id);
  const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
  const userOrgId = useCommonStore((s) => s.user?.organizationId);
  const homeOrgId = selectedOrgId || userOrgId || '';

  const { data: shareData, isLoading, isError, error, refetch } = useGetAgentShare(
    agentId,
    open && !!agentId
  );
  const { mutateAsync: saveShare, isPending: isSaving } = useSaveAgentShare();

  const [visibility, setVisibility] = useState<ResourceVisibility>(DEFAULT_RESOURCE_VISIBILITY);
  const [participants, setParticipants] = useState<ParticipantDraft[]>([]);
  const [orgPermission, setOrgPermission] = useState<SharePermissionType>('viewer');
  const [orgAllowSubagents, setOrgAllowSubagents] = useState(false);
  const [orgAllowSkills, setOrgAllowSkills] = useState(false);
  const [orgAllowTools, setOrgAllowTools] = useState(false);
  const [orgAllowKb, setOrgAllowKb] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [hydratedUserIds, setHydratedUserIds] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchQuery(value.trim());
      }, 350),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  useEffect(() => {
    if (!open) {
      setSearchInput('');
      setSearchQuery('');
      setUserPickerOpen(false);
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !shareData) return;

    const nextVisibility =
      shareData.visibility === 'public' || shareData.visibility === 'private'
        ? shareData.visibility
        : DEFAULT_RESOURCE_VISIBILITY;
    setVisibility(nextVisibility);

    const nextParticipants: ParticipantDraft[] = (shareData.participants || [])
      .map((p) => {
        const userId = p.subject?.userId;
        if (!userId) return null;
        const grant = p.grant || DEFAULT_SHARE_GRANT();
        return {
          userId,
          permissionType: grant.permissionType || 'viewer',
          allowSubagents: !!grant.allowSubagents,
          allowSkills: !!grant.allowSkills,
          allowTools: !!grant.allowTools,
          allowKb: !!grant.allowKb,
          displayName: userId,
        } satisfies ParticipantDraft;
      })
      .filter(Boolean) as ParticipantDraft[];

    setParticipants(nextParticipants);
    setHydratedUserIds(nextParticipants.map((p) => p.userId));

    const homeGrant =
      (shareData.orgGrants || []).find((g) => g.orgId === homeOrgId)?.grant ||
      (shareData.orgGrants || [])[0]?.grant;
    if (homeGrant) {
      setOrgPermission(homeGrant.permissionType || 'viewer');
      setOrgAllowSubagents(!!homeGrant.allowSubagents);
      setOrgAllowSkills(!!homeGrant.allowSkills);
      setOrgAllowTools(!!homeGrant.allowTools);
      setOrgAllowKb(!!homeGrant.allowKb);
    } else {
      setOrgPermission('viewer');
      setOrgAllowSubagents(false);
      setOrgAllowSkills(false);
      setOrgAllowTools(false);
      setOrgAllowKb(false);
    }
  }, [open, shareData, homeOrgId]);

  const shouldFetchUsers = open && visibility === 'private' && !!homeOrgId;

  const orgUserFilter = useMemo(
    () => ({
      organizationId: homeOrgId,
      IsOrgSpecific: true as const,
    }),
    [homeOrgId]
  );

  const { data: searchUsersData, isFetching: isSearching } = useGetUsersQuery({
    page: 0,
    pageSize: 30,
    filter: {
      ...orgUserFilter,
      ...(searchQuery
        ? searchQuery.includes('@')
          ? { email: searchQuery }
          : { name: searchQuery }
        : {}),
    },
    enabled: shouldFetchUsers,
  });

  const { data: hydratedUsersData } = useGetUsersQuery({
    page: 0,
    pageSize: Math.max(hydratedUserIds.length, 1),
    filter: hydratedUserIds.length
      ? { ...orgUserFilter, userIds: hydratedUserIds }
      : undefined,
    enabled: open && hydratedUserIds.length > 0 && !!homeOrgId,
  });

  useEffect(() => {
    const users = hydratedUsersData?.data;
    if (!users?.length) return;
    const byId = new Map(users.map((u) => [u.itemId, u]));
    setParticipants((prev) =>
      prev.map((p) => {
        const user = byId.get(p.userId);
        if (!user) return p;
        return {
          ...p,
          displayName: displayNameFor(user),
          email: user.email,
          avatarUrl: user.profileImageUrl,
        };
      })
    );
  }, [hydratedUsersData]);

  const searchResults = useMemo(() => {
    const users = searchUsersData?.data ?? [];
    const selected = new Set(participants.map((p) => p.userId));
    return users.filter(
      (u) => u.itemId !== currentUserId && !selected.has(u.itemId) && u.active !== false
    );
  }, [searchUsersData, participants, currentUserId]);

  const handleVisibilityChange = (value: ResourceVisibility) => {
    setVisibility(value);
    if (value === 'public') {
      setParticipants([]);
      setSearchInput('');
      setSearchQuery('');
      setUserPickerOpen(false);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const addParticipant = (user: IamData) => {
    if (visibility === 'public') return;
    setParticipants((prev) => {
      if (prev.some((p) => p.userId === user.itemId)) return prev;
      return [
        ...prev,
        {
          userId: user.itemId,
          permissionType: 'viewer',
          allowSubagents: false,
          allowSkills: false,
          allowTools: false,
          allowKb: false,
          displayName: displayNameFor(user),
          email: user.email,
          avatarUrl: user.profileImageUrl,
        },
      ];
    });
    clearSearch();
    setUserPickerOpen(false);
  };

  const addFromSearchInput = () => {
    const match = searchResults.find(
      (u) =>
        u.email?.toLowerCase() === searchInput.trim().toLowerCase() ||
        displayNameFor(u).toLowerCase() === searchInput.trim().toLowerCase()
    );
    if (match) {
      addParticipant(match);
      return;
    }
    setUserPickerOpen(true);
    searchInputRef.current?.focus();
  };

  const updateParticipant = (userId: string, patch: Partial<ParticipantDraft>) => {
    setParticipants((prev) =>
      prev.map((p) => {
        if (p.userId !== userId) return p;
        const next = { ...p, ...patch };
        if (next.permissionType === 'viewer') {
          next.allowSubagents = false;
          next.allowSkills = false;
          next.allowTools = false;
          next.allowKb = false;
        }
        return next;
      })
    );
  };

  const removeParticipant = (userId: string) => {
    setParticipants((prev) => prev.filter((p) => p.userId !== userId));
  };

  const buildOrgGrants = (): OrgGrantInput[] => {
    const guestGrants: OrgGrantInput[] = (shareData?.orgGrants || [])
      .filter((g) => g.orgId && g.orgId !== homeOrgId)
      .map((g) => ({
        orgId: g.orgId,
        permissionType: g.grant?.permissionType || 'viewer',
        allowSubagents: !!g.grant?.allowSubagents,
        allowSkills: !!g.grant?.allowSkills,
        allowTools: !!g.grant?.allowTools,
        allowKb: !!g.grant?.allowKb,
      }));

    if (visibility !== 'public' || !homeOrgId) {
      return guestGrants;
    }

    const isEditor = orgPermission === 'editor';
    return [
      {
        orgId: homeOrgId,
        permissionType: orgPermission,
        allowSubagents: isEditor && orgAllowSubagents,
        allowSkills: isEditor && orgAllowSkills,
        allowTools: isEditor && orgAllowTools,
        allowKb: isEditor && orgAllowKb,
      },
      ...guestGrants,
    ];
  };

  const handleSave = async () => {
    try {
      const payloadParticipants: ShareParticipantInput[] =
        visibility === 'public'
          ? []
          : participants.map((p) => {
              const grant = grantFromDraft(p);
              return {
                userId: p.userId,
                permissionType: grant.permissionType,
                allowSubagents: grant.allowSubagents,
                allowSkills: grant.allowSkills,
                allowTools: grant.allowTools,
                allowKb: grant.allowKb,
              };
            });

      const response = await saveShare({
        agentId,
        payload: {
          visibility,
          participants: payloadParticipants,
          orgGrants: buildOrgGrants(),
        },
      });

      if (response?.isSuccess === false) {
        toast({
          variant: 'destructive',
          title: shareText(t, 'ERROR', 'Error'),
          description:
            response.detail || shareText(t, 'FAILED_TO_SAVE', 'Failed to save share settings'),
        });
        return;
      }

      toast({
        variant: 'success',
        title: shareText(t, 'SUCCESS', 'Success'),
        description: shareText(t, 'SHARE_SETTINGS_SAVED', 'Share settings saved'),
      });
      onOpenChange(false);
    } catch (err) {
      const detail =
        err instanceof HttpError
          ? String(
              (err.error as { detail?: string })?.detail ||
                err.message ||
                shareText(t, 'FAILED_TO_SAVE', 'Failed to save share settings')
            )
          : shareText(t, 'FAILED_TO_SAVE', 'Failed to save share settings');
      toast({
        variant: 'destructive',
        title: shareText(t, 'ERROR', 'Error'),
        description: detail,
      });
    }
  };

  const loadErrorDetail =
    error instanceof HttpError
      ? String((error.error as { detail?: string })?.detail || error.message)
      : isError
        ? shareText(
            t,
            'FAILED_TO_LOAD_SHARE',
            'Failed to load share settings. Is the backend deployed?'
          )
        : null;

  const shareTitle = shareText(t, 'SHARE_AGENT', 'Share agent');
  const shareDescription = agentName
    ? shareText(t, 'SHARE_AGENT_DESCRIPTION', `Choose who can access "${agentName}".`, {
        name: agentName,
      })
    : shareText(
        t,
        'SHARE_AGENT_DESCRIPTION_GENERIC',
        'Choose visibility and who can access this agent.'
      );
  const publicHint = shareText(
    t,
    'SHARE_PUBLIC_HINT',
    'Everyone in the project can chat. Set org permission below. Individual people are cleared.'
  );
  const privateHint = shareText(
    t,
    'SHARE_PRIVATE_HINT',
    'Only you and people you add can access this agent.'
  );
  const viewOnlyLabel = shareText(t, 'SHARE_VIEW_ONLY', 'View only');
  const canEditLabel = shareText(t, 'SHARE_CAN_EDIT', 'Can edit');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-[560px] flex-col gap-0 overflow-hidden p-0 sm:rounded-xl">
        <DialogHeader className="shrink-0 space-y-1 border-b border-grey-50 px-6 py-4 dark:border-border">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-grey-700 dark:text-foreground">
            <ShareAndroid width={22} height={22} strokeWidth={1.5} />
            {shareTitle}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {shareDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {shareText(t, 'LOADING', 'Loading...')}
            </div>
          ) : loadErrorDetail ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-muted-foreground">{loadErrorDetail}</p>
              <Button type="button" variant="outline" onClick={() => refetch()}>
                {shareText(t, 'RETRY', 'Retry')}
              </Button>
            </div>
          ) : (
            <>
              <VisibilityField
                value={visibility}
                onValueChange={handleVisibilityChange}
                idPrefix="agent-share-visibility"
                showIcons
                description={visibility === 'public' ? publicHint : privateHint}
              />

              {visibility === 'public' ? (
                <div className="space-y-4 rounded-lg border border-grey-50 p-4 dark:border-border">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-grey-700 dark:text-foreground">
                      {shareText(t, 'PROJECT_ACCESS', 'Project access')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {shareText(
                        t,
                        'PROJECT_ACCESS_HINT',
                        'Default permission for everyone in your project who can see this public agent.'
                      )}
                    </p>
                  </div>
                  <Select
                    value={orgPermission}
                    onValueChange={(v) => setOrgPermission(v as SharePermissionType)}
                  >
                    <SelectTrigger className="h-10 rounded-lg">
                      <SelectValue placeholder={viewOnlyLabel} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">{viewOnlyLabel}</SelectItem>
                      <SelectItem value="editor">{canEditLabel}</SelectItem>
                    </SelectContent>
                  </Select>
                  {orgPermission === 'editor' ? (
                    <EditorAllowToggles
                      allowSubagents={orgAllowSubagents}
                      allowSkills={orgAllowSkills}
                      allowTools={orgAllowTools}
                      allowKb={orgAllowKb}
                      onChange={(key, value) => {
                        if (key === 'allowSubagents') setOrgAllowSubagents(value);
                        if (key === 'allowSkills') setOrgAllowSkills(value);
                        if (key === 'allowTools') setOrgAllowTools(value);
                        if (key === 'allowKb') setOrgAllowKb(value);
                      }}
                    />
                  ) : null}
                </div>
              ) : (
                <div className="space-y-5">
                  <Popover
                    modal={false}
                    open={userPickerOpen}
                    onOpenChange={setUserPickerOpen}
                  >
                    <PopoverAnchor asChild>
                      <div className="relative w-full">
                        <Input
                          ref={searchInputRef}
                          value={searchInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSearchInput(value);
                            debouncedSetSearch(value);
                            setUserPickerOpen(true);
                          }}
                          onFocus={() => setUserPickerOpen(true)}
                          onClick={() => setUserPickerOpen(true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addFromSearchInput();
                            }
                            if (e.key === 'Escape') {
                              setUserPickerOpen(false);
                            }
                          }}
                          placeholder={shareText(
                            t,
                            'SHARE_ADD_PERSON_PLACEHOLDER',
                            'Search by name or email'
                          )}
                          className="h-10 rounded-lg border-grey-100 pr-9 dark:border-border"
                          autoComplete="off"
                        />
                        {searchInput ? (
                          <button
                            type="button"
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              clearSearch();
                            }}
                            aria-label={shareText(t, 'CLEAR', 'Clear')}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setUserPickerOpen(true)}
                            aria-label={shareText(t, 'OPEN_USER_LIST', 'Open user list')}
                          >
                            <ChevronsUpDown className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </PopoverAnchor>
                    <PopoverContent
                      align="start"
                      side="bottom"
                      sideOffset={4}
                      collisionPadding={12}
                      onOpenAutoFocus={(e) => e.preventDefault()}
                      onCloseAutoFocus={(e) => e.preventDefault()}
                      onWheel={(e) => e.stopPropagation()}
                      className="z-[300] max-h-56 w-[var(--radix-popover-trigger-width)] overflow-y-auto overscroll-contain p-0"
                    >
                      {isSearching && !searchUsersData?.data?.length ? (
                        <div className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {shareText(t, 'SEARCHING', 'Searching...')}
                        </div>
                      ) : searchResults.length === 0 ? (
                        <p className="px-3 py-3 text-xs text-muted-foreground">
                          {shareText(t, 'NO_USERS_FOUND', 'No users found')}
                        </p>
                      ) : (
                        searchResults.map((user) => {
                          const name = displayNameFor(user);
                          return (
                            <button
                              key={user.itemId}
                              type="button"
                              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-grey-50 dark:hover:bg-white/10"
                              onClick={() => addParticipant(user)}
                            >
                              <UserInitials name={name} email={user.email} size="sm" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-grey-700 dark:text-foreground">
                                  {name}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {user.email}
                                </p>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </PopoverContent>
                  </Popover>

                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {shareText(t, 'PEOPLE_WITH_ACCESS', 'People with access')}
                    </p>
                    {participants.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-grey-100 px-3 py-8 text-center text-sm text-muted-foreground dark:border-border">
                        {shareText(
                          t,
                          'NO_PEOPLE_SHARED_YET',
                          'No one else has access yet. Pick someone above to add.'
                        )}
                      </p>
                    ) : (
                      <ul className="space-y-4">
                        {participants.map((person) => (
                          <li key={person.userId} className="space-y-2">
                            <div className="flex items-center gap-3">
                              {person.avatarUrl ? (
                                <Avatar className="h-9 w-9 shrink-0">
                                  <AvatarImage src={person.avatarUrl} alt="" />
                                  <AvatarFallback className="bg-grey-100 text-xs font-semibold text-grey-700 dark:bg-white/15 dark:text-grey-100">
                                    {initialsFor(person.displayName, person.email)}
                                  </AvatarFallback>
                                </Avatar>
                              ) : (
                                <UserInitials
                                  name={person.displayName}
                                  email={person.email}
                                  size="md"
                                />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-grey-700 dark:text-foreground">
                                  {person.displayName}
                                </p>
                                {person.email ? (
                                  <p className="truncate text-xs text-muted-foreground">
                                    {person.email}
                                  </p>
                                ) : null}
                              </div>
                              <Select
                                value={person.permissionType}
                                onValueChange={(v) =>
                                  updateParticipant(person.userId, {
                                    permissionType: v as SharePermissionType,
                                  })
                                }
                              >
                                <SelectTrigger className="h-9 w-[120px] shrink-0 rounded-lg">
                                  <SelectValue placeholder={viewOnlyLabel} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="editor">{canEditLabel}</SelectItem>
                                  <SelectItem value="viewer">{viewOnlyLabel}</SelectItem>
                                </SelectContent>
                              </Select>
                              <button
                                type="button"
                                className="rounded p-1.5 text-muted-foreground hover:bg-grey-50 hover:text-destructive dark:hover:bg-white/10"
                                onClick={() => removeParticipant(person.userId)}
                                aria-label={shareText(t, 'REMOVE', 'Remove')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            {person.permissionType === 'editor' ? (
                              <div className="ml-12">
                                <EditorAllowToggles
                                  allowSubagents={!!person.allowSubagents}
                                  allowSkills={!!person.allowSkills}
                                  allowTools={!!person.allowTools}
                                  allowKb={!!person.allowKb}
                                  onChange={(key, value) =>
                                    updateParticipant(person.userId, { [key]: value })
                                  }
                                />
                              </div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t border-grey-50 px-6 py-4 sm:justify-end dark:border-border">
          <Button
            type="button"
            variant="outline"
            className="rounded-[62px]"
            disabled={isSaving}
            onClick={() => onOpenChange(false)}
          >
            {shareText(t, 'CANCEL', 'Cancel')}
          </Button>
          <Button
            type="button"
            className="rounded-[62px]"
            disabled={isLoading || !!loadErrorDetail || isSaving}
            loading={isSaving}
            onClick={() => void handleSave()}
          >
            {shareText(t, 'SAVE', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type EditorAllowTogglesProps = {
  allowSubagents: boolean;
  allowSkills: boolean;
  allowTools: boolean;
  allowKb: boolean;
  onChange: (
    key: 'allowSubagents' | 'allowSkills' | 'allowTools' | 'allowKb',
    value: boolean
  ) => void;
};

function EditorAllowToggles({
  allowSubagents,
  allowSkills,
  allowTools,
  allowKb,
  onChange,
}: EditorAllowTogglesProps) {
  const { t } = useTranslation();
  const rows: Array<{
    key: 'allowSubagents' | 'allowSkills' | 'allowTools' | 'allowKb';
    label: string;
    checked: boolean;
  }> = [
    {
      key: 'allowSubagents',
      label: shareText(t, 'ALLOW_MANAGE_SUBAGENTS', 'Add or remove subagents'),
      checked: allowSubagents,
    },
    {
      key: 'allowSkills',
      label: shareText(t, 'ALLOW_MANAGE_SKILLS', 'Add or remove skills'),
      checked: allowSkills,
    },
    {
      key: 'allowTools',
      label: shareText(t, 'ALLOW_MANAGE_TOOLS', 'Add or remove tools'),
      checked: allowTools,
    },
    {
      key: 'allowKb',
      label: shareText(t, 'ALLOW_MANAGE_KB', 'Add or remove knowledge base'),
      checked: allowKb,
    },
  ];

  return (
    <div className="space-y-2.5 rounded-md bg-grey-50/80 px-3 py-3 dark:bg-white/5">
      <p className="text-xs font-medium text-muted-foreground">
        {shareText(t, 'EDITING_PERMISSIONS', 'Editing permissions')}
      </p>
      {rows.map((row) => (
        <label
          key={row.key}
          className="flex cursor-pointer items-center gap-2.5 text-sm text-grey-700 dark:text-foreground"
        >
          <Checkbox
            checked={row.checked}
            onCheckedChange={(checked) => onChange(row.key, checked === true)}
          />
          <span className="font-normal">{row.label}</span>
        </label>
      ))}
    </div>
  );
}
